import { Controller } from "@hotwired/stimulus"

/**
 * Toast Controller
 *
 * Manages stacking toast notifications with auto-dismiss and progress bar.
 * Replaces the basic flash auto-dismiss with a more polished experience.
 *
 * Usage (inline flash replacement):
 *   <div data-controller="toast"
 *        data-toast-type-value="success"
 *        data-toast-duration-value="5000"
 *        data-toast-message-value="Project saved successfully.">
 *   </div>
 *
 * Usage (programmatic from JS):
 *   document.dispatchEvent(new CustomEvent("toast:show", {
 *     detail: { message: "Saved!", type: "success", duration: 4000 }
 *   }))
 */
export default class extends Controller {
  static values = {
    type: { type: String, default: "success" },
    duration: { type: Number, default: 5000 },
    message: String
  }

  connect() {
    // If this is an inline flash element, convert it to a toast
    if (this.messageValue) {
      this.showToast(this.messageValue, this.typeValue, this.durationValue)
      this.element.remove()
      return
    }

    // Listen for programmatic toast events
    this.eventHandler = this.handleEvent.bind(this)
    document.addEventListener("toast:show", this.eventHandler)
  }

  disconnect() {
    if (this.eventHandler) {
      document.removeEventListener("toast:show", this.eventHandler)
    }
  }

  handleEvent(event) {
    const { message, type, duration } = event.detail
    this.showToast(message, type || "success", duration || 5000)
  }

  showToast(message, type, duration) {
    let container = document.querySelector(".toast-container")
    if (!container) {
      container = document.createElement("div")
      container.className = "toast-container"
      document.body.appendChild(container)
    }

    const toast = document.createElement("div")
    toast.className = `toast toast--${type}`
    toast.style.setProperty("--toast-duration", `${duration}ms`)

    toast.innerHTML = `
      <div class="toast-icon">${this.iconForType(type)}</div>
      <div class="toast-content">
        <p class="toast-message">${this.escapeHtml(message)}</p>
      </div>
      <button class="toast-dismiss" aria-label="Dismiss">
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
      <div class="toast-progress">
        <div class="toast-progress-bar"></div>
      </div>
    `

    // Dismiss on click
    toast.querySelector(".toast-dismiss").addEventListener("click", () => {
      this.dismissToast(toast)
    })

    container.appendChild(toast)

    // Auto-dismiss — track remaining time so hover pause preserves correct duration
    let dismissAt = Date.now() + duration
    let remaining = duration

    this._timeout = setTimeout(() => {
      this.dismissToast(toast)
    }, duration)

    // Pause on hover — capture remaining ms at pause point
    toast.addEventListener("mouseenter", () => {
      clearTimeout(this._timeout)
      remaining = Math.max(0, dismissAt - Date.now())
      const bar = toast.querySelector(".toast-progress-bar")
      if (bar) bar.style.animationPlayState = "paused"
    })

    toast.addEventListener("mouseleave", () => {
      dismissAt = Date.now() + remaining
      const bar = toast.querySelector(".toast-progress-bar")
      if (bar) bar.style.animationPlayState = "running"
      this._timeout = setTimeout(() => {
        this.dismissToast(toast)
      }, remaining)
    })
  }

  dismissToast(toast) {
    toast.classList.add("toast--exiting")
    setTimeout(() => {
      toast.remove()
      // Remove container if empty
      const container = document.querySelector(".toast-container")
      if (container && container.children.length === 0) {
        container.remove()
      }
    }, 200)
  }

  iconForType(type) {
    const icons = {
      success: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
      error: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>',
      warning: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/></svg>',
      info: '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>'
    }
    return icons[type] || icons.success
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
